import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { unitAPI, propertyAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  DoorOpen,
  Bed,
  Bath,
  SquareStack,
  DollarSign,
  Edit
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Units = () => {
  const { t } = useI18n();
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [formData, setFormData] = useState({
    property_id: '',
    unit_number: '',
    bedrooms: 1,
    bathrooms: 1,
    square_feet: '',
    rent_amount: '',
    deposit_amount: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [unitsRes, propertiesRes] = await Promise.all([
        unitAPI.getAll(),
        propertyAPI.getAll()
      ]);
      setUnits(unitsRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: properties[0]?.id || '',
      unit_number: '',
      bedrooms: 1,
      bathrooms: 1,
      square_feet: '',
      rent_amount: '',
      deposit_amount: '',
      description: ''
    });
    setEditingUnit(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseFloat(formData.bathrooms),
        square_feet: formData.square_feet ? parseInt(formData.square_feet) : null,
        rent_amount: parseFloat(formData.rent_amount),
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null
      };

      if (editingUnit) {
        await unitAPI.update(editingUnit.id, submitData);
        toast.success('Unit updated successfully');
      } else {
        await unitAPI.create(submitData);
        toast.success('Unit created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save unit');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      property_id: unit.property_id,
      unit_number: unit.unit_number,
      bedrooms: unit.bedrooms,
      bathrooms: unit.bathrooms,
      square_feet: unit.square_feet || '',
      rent_amount: unit.rent_amount,
      deposit_amount: unit.deposit_amount || '',
      description: unit.description || ''
    });
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const filteredUnits = selectedProperty === 'all' 
    ? units 
    : units.filter(u => u.property_id === selectedProperty);

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property?.name || 'Unknown';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OCCUPIED': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'VACANT': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'MAINTENANCE': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('units.title')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('units.title')}>
      <div className="space-y-6" data-testid="units-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-muted-foreground">{t('units.subtitle')}</p>
          </div>
          <div className="flex gap-4">
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-48 bg-card border-[#e5e7eb]" data-testid="property-filter">
                <SelectValue placeholder="Filter by property" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gold text-white hover:bg-gold-light"
                  disabled={properties.length === 0}
                  data-testid="add-unit-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Unit
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] bg-card border-border">
                <DialogHeader>
                  <DialogTitle className="font-serif">
                    {editingUnit ? 'Edit Unit' : 'Add New Unit'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Property</Label>
                    <Select 
                      value={formData.property_id} 
                      onValueChange={(v) => setFormData({...formData, property_id: v})}
                      disabled={editingUnit}
                    >
                      <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="unit-property-select">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Unit Number</Label>
                      <Input
                        name="unit_number"
                        value={formData.unit_number}
                        onChange={handleChange}
                        className="bg-background border-[#e5e7eb]"
                        placeholder="101"
                        required
                        data-testid="unit-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Sq Ft</Label>
                      <Input
                        name="square_feet"
                        type="number"
                        value={formData.square_feet}
                        onChange={handleChange}
                        className="bg-background border-[#e5e7eb]"
                        placeholder="850"
                        data-testid="unit-sqft-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Bedrooms</Label>
                      <Select 
                        value={String(formData.bedrooms)} 
                        onValueChange={(v) => setFormData({...formData, bedrooms: v})}
                      >
                        <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="unit-bedrooms-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5].map(n => (
                            <SelectItem key={n} value={String(n)}>{n === 0 ? 'Studio' : n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Bathrooms</Label>
                      <Select 
                        value={String(formData.bathrooms)} 
                        onValueChange={(v) => setFormData({...formData, bathrooms: v})}
                      >
                        <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="unit-bathrooms-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 1.5, 2, 2.5, 3, 3.5, 4].map(n => (
                            <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Monthly Rent ($)</Label>
                      <Input
                        name="rent_amount"
                        type="number"
                        step="0.01"
                        value={formData.rent_amount}
                        onChange={handleChange}
                        className="bg-background border-[#e5e7eb]"
                        placeholder="1500"
                        required
                        data-testid="unit-rent-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground text-sm uppercase tracking-wider">Deposit ($)</Label>
                      <Input
                        name="deposit_amount"
                        type="number"
                        step="0.01"
                        value={formData.deposit_amount}
                        onChange={handleChange}
                        className="bg-background border-[#e5e7eb]"
                        placeholder="1500"
                        data-testid="unit-deposit-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="save-unit-btn">
                    {editingUnit ? 'Update Unit' : 'Create Unit'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Units Grid */}
        {properties.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <DoorOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('units.addPropertyFirst')}</h3>
              <p className="text-muted-foreground">
                {t('units.addPropertyFirstDesc')}
              </p>
            </CardContent>
          </Card>
        ) : filteredUnits.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <DoorOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">{t('units.noUnits')}</h3>
              <p className="text-muted-foreground mb-6">
                {t('units.noUnitsDesc')}
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-gold text-white hover:bg-gold-light">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Unit
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUnits.map((unit, index) => (
              <Card 
                key={unit.id} 
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`unit-card-${unit.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                        {getPropertyName(unit.property_id)}
                      </p>
                      <h3 className="font-serif text-xl">Unit {unit.unit_number}</h3>
                    </div>
                    <span className={`px-3 py-1 text-xs uppercase tracking-wider border ${getStatusColor(unit.status)}`}>
                      {unit.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Bed className="w-4 h-4" />
                      <span className="text-sm">{unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} Bed`}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Bath className="w-4 h-4" />
                      <span className="text-sm">{unit.bathrooms} Bath</span>
                    </div>
                    {unit.square_feet && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <SquareStack className="w-4 h-4" />
                        <span className="text-sm">{unit.square_feet} sqft</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-gold" />
                      <span className="text-xl font-semibold">${unit.rent_amount.toLocaleString()}</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleEdit(unit)}
                      data-testid={`edit-unit-${unit.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Units;
